import { useCallback, useEffect } from 'react';
import { PropertyFilterProps } from '@cloudscape-design/components';

/**
 * Bridge hook between Cloudscape PropertyFilter UI and filter persistence
 *
 * Settings pages use Cloudscape's PropertyFilter component which manages its own UI state
 * via useGenericFilter. This hook bridges that UI state with useFilterPersistence to enable
 * sessionStorage persistence and synchronization on page reload.
 *
 * Architecture:
 * - Main pages (Audits, Questions, Tasks) use custom filters → useFilterPersistence directly
 * - Settings pages (Users, Templates, Teams, etc.) use Cloudscape PropertyFilter → need this bridge
 *
 * What it does:
 * 1. Wraps setSearchText to update both UI state and sessionStorage
 * 2. Syncs search text from sessionStorage to UI on page reload
 * 3. Syncs query tokens from sessionStorage to UI on page reload (when setQuery provided)
 *
 * @param setSearchTextInternal - Setter from useGenericFilter (UI state for search text)
 * @param setQueryFilters - Setter from useFilterPersistence (sessionStorage persistence)
 * @param queryFilters - Current persisted filters (reloaded from sessionStorage on page reload)
 * @param setQuery - Optional setter for query state (UI state for PropertyFilter tokens)
 * @returns Wrapped setSearchText function that updates both UI and sessionStorage
 */
export function usePropertyFilterPersistence(
  setSearchTextInternal: (text: string) => void,
  setQueryFilters: (updater: (prev: any) => any) => void,
  queryFilters: {
    searchText?: string;
    tokens?: PropertyFilterProps.Token[];
    operation?: 'and' | 'or';
  },
  setQuery?: (query: PropertyFilterProps.Query) => void,
) {
  // Wrapper to persist search text changes to both UI and sessionStorage
  const setSearchText = useCallback(
    (text: string) => {
      setSearchTextInternal(text);
      setQueryFilters((prev: any) => ({ ...prev, searchText: text }));
    },
    [setSearchTextInternal, setQueryFilters],
  );

  // Sync search text from sessionStorage to UI on page reload
  useEffect(() => {
    setSearchTextInternal(queryFilters.searchText || '');
  }, [queryFilters.searchText, setSearchTextInternal]);

  // Sync query tokens from sessionStorage to UI on page reload
  useEffect(() => {
    if (setQuery && queryFilters && queryFilters.tokens) {
      setQuery({
        tokens: queryFilters.tokens,
        operation: (queryFilters as any).operation || 'and',
      });
    }
  }, [queryFilters, setQuery]);

  return setSearchText;
}
